/**
 * MANAGEMENT (14) · Pricing + billing data layer — the section-owned READ hooks + WRITE mutations for the
 * cross-lab pricing/billing console. ADDITIVE to the shell ../api.ts: it REUSES the shell's shared `api`
 * verb-client, `useApiQuery`/`useApiMutation`, the cross-lab `managementKeys` namespace convention, and the
 * `isManagementAbsent` helper — it re-declares NONE of the core. Kept in this NEW co-located file so the
 * shell ../api.ts stays untouched while this section is built (the documented "section agent imports from
 * here" convention).
 *
 * Server contract — code-verified against server/src/modules/management/pricing.{routes,validators,
 * controller}.js (ALL on the `platformRoute` chain, capability `management.pricing.manage`, NO tenant scope,
 * cross-lab reads via rawPrisma):
 *   GET    /management/pricing/plans                          list the catalogue (+ tier / actif facets)
 *   GET    /management/pricing/plans/:code                    read one plan
 *   POST   /management/pricing/plans                          publish a NEW tier (create)
 *   PUT    /management/pricing/plans/:code                    REISSUE the same code (correct-by-reissue)
 *   DELETE /management/pricing/plans/:code                    RETIRE (soft supersede; row survives)
 *   POST   /management/pricing/plans/:code/restore            reinstate a retired plan
 *   POST   /management/pricing/labs/:laboratoireId/assign     assign a plan → drives the projection + gate
 *   POST   /management/billing/labs/:laboratoireId/checkout   create a Chargily/simulated checkout
 *   GET    /management/billing/labs/:laboratoireId/payments   a lab's payment history (newest first)
 *
 * CROSS-LAB, NOT lab-scoped — every key lives under the shared `["platform","management","pricing",…]`
 * namespace (a lab switch never invalidates it; one operator session reads the whole estate), mirroring the
 * sibling `managementKeys` factory. A publish/reissue/retire shifts the list AND the pricing table, so the
 * mutations invalidate the pricing ROOT (not one filter key); a payment/checkout shifts one lab's billing
 * history, so those invalidate that lab's payments key.
 */
import { api, useApiMutation, useApiQuery } from "../api";
import type {
  AssignPlanBody,
  CheckoutResult,
  CreateCheckoutBody,
  CreatePlanBody,
  PaymentList,
  PlanList,
  PlanRow,
  PlanTier,
  UpdatePlanBody,
} from "./pricing.types";

/** The pricing/billing query-key factory — rooted under the platform/management namespace, NOT lab-scoped. */
export const pricingKeys = {
  root: ["platform", "management", "pricing"] as const,
  plans: (tier?: PlanTier, actifSeulement?: boolean) =>
    ["platform", "management", "pricing", "plans", { tier: tier ?? null, actifSeulement: !!actifSeulement }] as const,
  payments: (laboratoireId: string) =>
    ["platform", "management", "pricing", "payments", laboratoireId] as const,
};

/** Encode a plan code for the URL path (codes are uppercased alnum, but encode defensively all the same). */
function codePath(code: string): string {
  return encodeURIComponent(code);
}

// ── Reads ──────────────────────────────────────────────────────────────────────────

/**
 * GET /management/pricing/plans — the pricing catalogue, optionally faceted by tier + actif-only. The
 * operator page passes `actifSeulement=false` (sees retired tiers too, greyed) — a lab pricing table would
 * pass true. 30s staleTime keeps the table warm without hammering the estate.
 */
export function usePlans(enabled: boolean, opts: { tier?: PlanTier; actifSeulement?: boolean } = {}) {
  const query: Record<string, string | boolean> = {};
  if (opts.tier) query.tier = opts.tier;
  if (opts.actifSeulement) query.actif_seulement = true;
  return useApiQuery<PlanList>(pricingKeys.plans(opts.tier, opts.actifSeulement), "/management/pricing/plans", {
    query,
    enabled,
    staleTime: 30_000,
  });
}

/**
 * GET /management/billing/labs/:laboratoireId/payments — one lab's payment history, newest first. Enabled
 * only once a lab is selected (an empty id → disabled). 15s staleTime — a settlement lands via the webhook,
 * so a shorter window keeps the ledger fresh after a checkout.
 */
export function useLabPayments(laboratoireId: string, enabled: boolean) {
  return useApiQuery<PaymentList>(
    pricingKeys.payments(laboratoireId),
    `/management/billing/labs/${laboratoireId}/payments`,
    { enabled: enabled && !!laboratoireId, staleTime: 15_000 },
  );
}

// ── Plan catalogue mutations (full CRUD by reissue/supersede, L-1) ───────────────────

/** POST /management/pricing/plans — publish a NEW tier. A code collision surfaces as a typed CONFLICT. */
export function useCreatePlan() {
  return useApiMutation<PlanRow, CreatePlanBody>(
    (body) => api.post<PlanRow>("/management/pricing/plans", body),
    { invalidate: [pricingKeys.root] },
  );
}

/** PUT /management/pricing/plans/:code — REISSUE the same code (partial patch; the server bumps version). */
export function useUpdatePlan() {
  return useApiMutation<PlanRow, { code: string; body: UpdatePlanBody }>(
    ({ code, body }) => api.put<PlanRow>(`/management/pricing/plans/${codePath(code)}`, body),
    { invalidate: [pricingKeys.root] },
  );
}

/** DELETE /management/pricing/plans/:code — RETIRE (soft supersede; the row survives for old payment refs). */
export function useRetirePlan() {
  return useApiMutation<PlanRow, { code: string }>(
    ({ code }) => api.delete<PlanRow>(`/management/pricing/plans/${codePath(code)}`),
    { invalidate: [pricingKeys.root] },
  );
}

/** POST /management/pricing/plans/:code/restore — reinstate a retired plan. Idempotent server-side. */
export function useRestorePlan() {
  return useApiMutation<PlanRow, { code: string }>(
    ({ code }) => api.post<PlanRow>(`/management/pricing/plans/${codePath(code)}/restore`, {}),
    { invalidate: [pricingKeys.root] },
  );
}

// ── Assign + billing mutations ───────────────────────────────────────────────────────

/**
 * POST /management/pricing/labs/:laboratoireId/assign — assign a catalogued plan to a lab. Delegates
 * server-side to the subscriptions projection writer (drives the on-box entitlement gate). Invalidates the
 * whole management estate — the assignment shifts the sibling subscriptions directory too.
 */
export function useAssignPlan(laboratoireId: string) {
  return useApiMutation<{ plan: PlanRow; subscription: unknown }, AssignPlanBody>(
    (body) => api.post(`/management/pricing/labs/${laboratoireId}/assign`, body),
    { invalidate: [["platform", "management"]] },
  );
}

/**
 * POST /management/billing/labs/:laboratoireId/checkout — create a Chargily (test-mode when the env key is
 * set) or SIMULATED checkout. Writes a pending SubscriptionPayment then returns the checkout URL. Invalidates
 * that lab's payment history so the new pending row appears immediately.
 */
export function useCreateCheckout(laboratoireId: string) {
  return useApiMutation<CheckoutResult, CreateCheckoutBody>(
    (body) => api.post<CheckoutResult>(`/management/billing/labs/${laboratoireId}/checkout`, body),
    { invalidate: [pricingKeys.payments(laboratoireId)] },
  );
}

// Re-export the shell absent-module helper so the screen wires its whole data layer from this ONE module
// (the shell's documented "section agent imports from here" convention).
export { isManagementAbsent } from "../api";
